/**
 * FishingMeter — HUD overlay showing the local player's fishing state.
 *
 * Displays the current fishing status (idle/casting/waiting/bite/reeling/
 * caught/missed) with a context-sensitive action button. Only visible
 * during a live fishing event when the local player has a fishing state.
 *
 * @see docs/design/gm/GM_EVENT_PHASE4_SPEC.md §3.1
 */

import React from 'react';
import { useGmEvent } from '../../../services/GmEventService';
import { COLORS, FONTS } from '../../theme';

function getLocalSessionId(): string | null {
  return (window as any).nm?.getRoom?.()?.sessionId ?? null;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; action: 'cast' | 'reel' | null }> = {
  idle:     { label: 'Ready to Fish',                    color: COLORS.textMuted,     action: 'cast' },
  casting:  { label: 'Casting...',                       color: COLORS.warning,       action: null },
  waiting:  { label: 'Waiting for a bite...',            color: COLORS.textSecondary, action: null },
  bite:     { label: 'BITE! Reel now!',                  color: COLORS.success,       action: 'reel' },
  reeling:  { label: 'Reeling in...',                    color: COLORS.warning,       action: null },
  caught:   { label: 'Caught! Cast again?',              color: COLORS.textAccent,    action: 'cast' },
  missed:   { label: 'Got away... Cast again?',          color: COLORS.danger,        action: 'cast' },
};

export const FishingMeter: React.FC = () => {
  const { announcement, fishingPlayerState, actions } = useGmEvent();

  if (!announcement || announcement.phase !== 'live') return null;
  if (!fishingPlayerState) return null;

  const localSid = getLocalSessionId();
  if (fishingPlayerState.sessionId !== localSid) return null;

  const config = STATUS_CONFIG[fishingPlayerState.status] ?? STATUS_CONFIG.idle;

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: 80,
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '8px 16px',
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    fontFamily: FONTS.ui,
    zIndex: 100,
    pointerEvents: 'auto',
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 20px',
    background: config.action === 'reel' ? COLORS.success : COLORS.surfaceRaised,
    border: `1px solid ${config.action === 'reel' ? COLORS.success : COLORS.border}`,
    borderRadius: 4,
    color: config.action === 'reel' ? '#000' : COLORS.textPrimary,
    cursor: 'pointer',
    fontSize: 14,
    fontFamily: FONTS.ui,
    fontWeight: 'bold',
  };

  return (
    <div style={containerStyle}>
      <div style={{ fontSize: 13, fontWeight: 'bold', color: config.color }}>
        {config.label}
      </div>
      {config.action && (
        <button
          style={buttonStyle}
          onClick={() => {
            if (config.action === 'reel') actions.fishingReel(announcement.eventId);
            else actions.fishingCast(announcement.eventId);
          }}
        >
          {config.action === 'reel' ? 'REEL!' : 'Cast'}
        </button>
      )}
      <div style={{ fontSize: 11, color: COLORS.textSecondary, display: 'flex', gap: 12 }}>
        <span>Score: {fishingPlayerState.totalScore}</span>
        <span>Weight: {fishingPlayerState.totalWeight.toFixed(1)}kg</span>
      </div>
    </div>
  );
};
